import type * as Highcharts from 'highcharts'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { GridConfig } from '../../grid/types'
import { ReactGrid } from '../../grid'
import type { LinkedEndpoints, LinkedTargetState } from '../api/types'
import { textAnalyticsBus } from '../bus/textAnalyticsBus'
import { TextAnalyticsChart } from '../chart/TextAnalyticsChart'

declare global {
	interface Window {
		__taChartOptions?: Record<number, Highcharts.Options>
	}
}

type Props = {
	blockId: number
	initialState: LinkedTargetState
	placeholderHtml: string
	endpoints: LinkedEndpoints | Record<string, never>
	csvFilename: string
	/** Highcharts chart type (e.g. 'column' for snapshot, 'spline' for trend) used to pick the `.graphics-container .highcharts-type-X` CSS hook for tooltip styling. */
	chartType?: string
}

const installChartScript = (script: string, blockId: number): boolean => {
	try {
		const trimmed = script.trim()
		// The script may be either a bare options literal (e.g. "{...}") or a full assignment
		// statement (e.g. "window.__taChartOptions[3018] = ({...});"). Detect which.
		if (trimmed.startsWith('{') || trimmed.startsWith('(')) {
			const opts = new Function(`return (${trimmed})`)() as Highcharts.Options
			window.__taChartOptions = window.__taChartOptions ?? {}
			window.__taChartOptions[blockId] = opts
		} else {
			// Statement form — executing populates window.__taChartOptions for us.
			new Function(trimmed)()
		}
		return true
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error('[LinkedReportContainer] failed to install chart script', e)
		return false
	}
}

export const LinkedReportContainer: React.FC<Props> = ({
	blockId,
	initialState,
	placeholderHtml,
	endpoints: initialEndpoints,
	csvFilename: initialCsvFilename,
	chartType = 'column',
}) => {
	const [state, setState] = useState<LinkedTargetState>(initialState)
	const [endpoints, setEndpoints] = useState<LinkedEndpoints | Record<string, never>>(initialEndpoints)
	const [csvFilename, setCsvFilename] = useState<string>(initialCsvFilename)
	const [gridConfig, setGridConfig] = useState<GridConfig | undefined>(
		initialState.displayType === 'grid' ? initialState.gridConfig : undefined,
	)
	const [chartKey, setChartKey] = useState(0)
	const [pendingChartLoad, setPendingChartLoad] = useState(false)
	const loadCancelRef = useRef<boolean>(false)

	useEffect(() => {
		return textAnalyticsBus.subscribe(blockId, (next) => {
			// Adopt endpoints/csv from the live bus event (the click handler computes them
			// against the freshly-set $config on the linked block, so URLs include the right
			// persistent state).
			if (next.endpoints !== undefined) {
				setEndpoints(next.endpoints)
			}
			if (next.csvFilename !== undefined) {
				setCsvFilename(next.csvFilename)
			}
			if (next.displayType === 'grid' && next.gridConfig !== undefined) {
				setGridConfig(next.gridConfig)
			}
			setState(next)

			if (next.displayType !== 'chart') {
				setChartKey((k) => k + 1)
				return
			}

			// Chart state — prefer the inline chartScript from the bus event (rendered by PHP
			// via BaseGraph::renderGraph(), preserving JS expression handlers and full styling).
			// Fall back to the chartData JSON endpoint if no inline script was provided.
			if (window.__taChartOptions !== undefined) {
				delete window.__taChartOptions[blockId]
			}

			if (next.chartScript !== undefined) {
				const ok = installChartScript(next.chartScript, blockId)
				if (ok) {
					setPendingChartLoad(false)
					setChartKey((k) => k + 1)
					return
				}
			}

			setPendingChartLoad(true)
			loadCancelRef.current = false
			const chartDataUrl = (next.endpoints ?? initialEndpoints).chartData
			if (chartDataUrl === undefined || chartDataUrl === '') {
				// eslint-disable-next-line no-console
				console.warn('[LinkedReportContainer] chartData endpoint missing for block', blockId)
				setPendingChartLoad(false)
				return
			}

			fetch(chartDataUrl, { headers: { Accept: 'application/json' } })
				.then((r) => r.json())
				.then((payload: { script?: string }) => {
					if (loadCancelRef.current) {
						return
					}
					if (payload.script !== undefined) {
						installChartScript(payload.script, blockId)
					}
					setPendingChartLoad(false)
					setChartKey((k) => k + 1)
				})
				.catch((e: unknown) => {
					if (!loadCancelRef.current) {
						// eslint-disable-next-line no-console
						console.error('[LinkedReportContainer] chartData fetch failed', e)
						setPendingChartLoad(false)
					}
				})
		})
	}, [blockId, initialEndpoints])

	useEffect(() => {
		return () => {
			loadCancelRef.current = true
		}
	}, [])

	const eps = endpoints as LinkedEndpoints

	// Memoise sub-endpoint objects so prop identity is stable across re-renders.
	// ReactGrid (and TextAnalyticsChart) memoise their API clients on the endpoints
	// reference; constructing a fresh object literal here would force them to rebuild
	// on every parent render and re-fire their data-fetch effects in a loop.
	const gridEndpoints = useMemo(
		() => ({
			fetch: eps.gridFetch,
			action: eps.gridAction,
			groupAction: eps.gridGroupAction,
			export: eps.gridExport,
		}),
		[eps.gridFetch, eps.gridAction, eps.gridGroupAction, eps.gridExport],
	)

	const chartEndpoints = useMemo(
		() => ({ click: eps.chartClick, data: eps.chartData }),
		[eps.chartClick, eps.chartData],
	)

	if (state.displayType === 'text') {
		return <div className="ta-linked-text" dangerouslySetInnerHTML={{ __html: placeholderHtml }} />
	}

	if (state.displayType === 'grid') {
		if (gridConfig === undefined) {
			return <div className="ta-loading">Loading…</div>
		}
		return <ReactGrid config={gridConfig} endpoints={gridEndpoints} />
	}

	if (pendingChartLoad) {
		return <div className="ta-loading">Loading…</div>
	}

	return (
		<div className={`graphics-container highcharts-type-${chartType} highcharts-height`}>
			<TextAnalyticsChart key={chartKey} blockId={blockId} endpoints={chartEndpoints} />
		</div>
	)
}
